import type { MasteryMapDocument, MasteryMapListItem } from '@ts-mastery-map/interfaces'
import type { MasterySkill, MasteryTest, MasteryTestSnapshot } from '@app/types/mastery-map-dashboard/interfaces'
import type { MasterySkillStatus } from '@app/types/mastery-map-dashboard/types'

import { AssessmentType } from '@brighterly/lib-core-types/student-assessment/enums'
import { MasteryLevel } from '@ts-mastery-map/enums'

import { getStrandCode } from '@utils/mastery-map/strand-titles'

const SKILL_STATUS_BY_LEVEL: Record<MasteryLevel, MasterySkillStatus> = {
  [MasteryLevel.Mastered]: 'mastered',
  [MasteryLevel.Developing]: 'developing',
  [MasteryLevel.NeedsSupport]: 'developing',
  [MasteryLevel.NotAssessed]: 'up-next',
}

const NOT_ASSESSED_STAT_KEY = 'not_assessed'
const BASELINE_LABEL = 'Baseline'
const BASELINE_RESULTS_TITLE = 'Baseline results'

const isBaselineItem = (item: MasteryMapListItem): boolean => item.type === AssessmentType.Baseline

/** The list arrives newest first; tests after Baseline are numbered by their position. */
export const getMasteryTests = (list: MasteryMapListItem[]): MasteryTest[] => {
  const oldestFirst = [...list].reverse()

  return oldestFirst.map((item, index) => {
    const isBaseline = isBaselineItem(item)
    const number = oldestFirst.slice(0, index + 1).filter(entry => !isBaselineItem(entry)).length

    return {
      id: item.assessment_id,
      label: isBaseline ? BASELINE_LABEL : `Test ${number}`,
      resultsTitle: isBaseline ? BASELINE_RESULTS_TITLE : `Test results #${number}`,
      isBaseline,
      generatedAt: item.generated_at,
      isViewed: item.is_viewed !== false,
    }
  })
}

const parseCount = (value: string): number => Number.parseInt(value, 10) || 0

const getNotAssessedCount = (document: MasteryMapDocument): number => {
  const stat = document.stats.find(entry => entry.key === NOT_ASSESSED_STAT_KEY)

  return stat ? parseCount(stat.value) : 0
}

const toAssessedSkills = (document: MasteryMapDocument): MasterySkill[] =>
  document.standards.map(standard => ({
    code: standard.code,
    label: standard.label,
    strand: getStrandCode(standard.code),
    status: SKILL_STATUS_BY_LEVEL[standard.level],
  }))

const toUpNextSkills = (document: MasteryMapDocument): MasterySkill[] =>
  (document.not_assessed ?? []).map(skill => ({
    code: skill.code,
    label: skill.label,
    strand: getStrandCode(skill.code),
    status: 'up-next',
  }))

export const getTestSnapshot = (test: MasteryTest, document: MasteryMapDocument): MasteryTestSnapshot => {
  const skills = [...toAssessedSkills(document), ...toUpNextSkills(document)]
  const listedUpNext = skills.filter(skill => skill.status === 'up-next').length

  return {
    test,
    skills,
    upNextCount: document.not_assessed ? listedUpNext : Math.max(getNotAssessedCount(document), listedUpNext),
  }
}
